// Profilo.tsx — il proprio account: nome, piano, partite di oggi, uscita, i miei dati, cancellazione.
import { InterruttoreTestoGrande } from "../componenti/TestoGrande.tsx";
import { InterruttoreNotifiche } from "../componenti/Notifiche.tsx";
import { Palco } from "../componenti/Palco.tsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Finestra } from "../componenti/Finestra.tsx";
import { aggiornaIo, cambiaNome, cancellaAccount, esci, scaricaDati, useAccount, type Io } from "../servizi/account.ts";
import { ErroreRete } from "../servizi/api.ts";
import { NOME_MAX } from "../../../condiviso/protocollo.ts";
import { Identita } from "./Identita.tsx";
import { SchedaLiberatorie } from "./SchedaLiberatorie.tsx";
import { LinkPersonale, SchedaClassifica, SchedaCrediti, SchedaScelte, SchedaStatistiche } from "./ProfiloSocial.tsx";
import { vai } from "../servizi/percorso.ts";
import { eBooleano, leggiImpostazione } from "../servizi/impostazioni.ts";
import { IMP_APP } from "../costanti.ts";
import { Indietro } from "../componenti/Indietro.tsx";
import { Avatar } from "../componenti/Avatar.tsx";
import { useAvatar } from "../servizi/account.ts";
import { PERCORSO_AVATAR } from "../costanti.ts";

export function Profilo({ onIndietro, onPiani }: { onIndietro: () => void; onPiani: () => void }) {
  const { t } = useTranslation();
  const account = useAccount();

  useEffect(() => {
    void aggiornaIo().catch(() => undefined);
  }, []);
  useEffect(() => {
    if (account.tipo === "ospite") onIndietro();
  }, [account.tipo, onIndietro]);

  return (
    <div className="pagina pagina-account ld-pagina">
      <header className="ld-barra">
        <Indietro onClick={onIndietro} />
        <span />
      </header>
      {account.tipo === "registrato" ? (
        <ContenutoProfilo io={account.io} onPiani={onPiani} />
      ) : (
        <p className="nota-centrata" aria-busy="true">
          {t("generale.caricamento")}
        </p>
      )}
    </div>
  );
}

function ContenutoProfilo({ io, onPiani }: { io: Io; onPiani: () => void }) {
  const { t, i18n } = useTranslation();
  const [nome, setNome] = useState(io.nome);
  const [errore, setErrore] = useState<string | null>(null);
  const [salvato, setSalvato] = useState(false);
  const [cancella, setCancella] = useState(false);
  const data = new Intl.DateTimeFormat(i18n.language, { dateStyle: "long" });
  const goldInVendita = leggiImpostazione(IMP_APP.goldInVendita, false, eBooleano);
  const avatar = useAvatar();
  const azione = (f: () => Promise<void>) => () => {
    setErrore(null);
    f().catch((e: unknown) => setErrore(e instanceof ErroreRete && ["offline", "timeout"].includes(e.codice) ? e.codice : "generico"));
  };

  return (
    <main className="account-centro profilo">
      {/* v019: il mio avatar in grande, con l'anello; un tocco per cambiarlo. */}
      <Palco
        colore={io.piano === "gold" ? "oro" : "primario"}
        compatto
        titolo={io.nome}
        figura={
          <button type="button" className="ld-profilo-avatar" aria-label={t("avatar.cambia")} onClick={() => vai(PERCORSO_AVATAR)}>
            <Avatar quale={avatar} dimensione={132} anello classe="ld-palco-mascotte" />
            {/* v033: la matita fa capire che l'avatar (e il colore) si cambiano toccandolo */}
            <span className="ld-avatar-matita" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4" />
              </svg>
            </span>
            <span className={`ld-etichetta ld-etichetta-${io.piano === "base" ? "base" : io.piano}`}>{t(`piani.nome_${io.piano}`)}</span>
          </button>
        }
      />

      <section className="scheda-account">
        <form
          className="riga-nome"
          onSubmit={(e) => {
            e.preventDefault();
            setSalvato(false);
            azione(async () => {
              await cambiaNome(nome.trim());
              setSalvato(true);
            })();
          }}
        >
          <label className="campo-etichettato">
            <span className="etichetta">{t("account.nome")}</span>
            <input className="campo" value={nome} maxLength={NOME_MAX} onChange={(e) => setNome(e.target.value)} required />
          </label>
          {/* v040: «Salva» solo quando il nome è cambiato */}
          {nome.trim() !== io.nome && (
            <button type="submit" className="pulsante pulsante-secondario">
              {t("account.salva")}
            </button>
          )}
          {salvato && <span role="status">{t("account.salvato")}</span>}
        </form>
        <Identita io={io} />
      </section>

      <LinkPersonale io={io} />
      <SchedaStatistiche />
      <SchedaCrediti io={io} />
      {/* v040: i consensi tutti insieme, dati una volta e cambiabili qui */}
      <SchedaScelte io={io} />
      <SchedaClassifica io={io} />
      <SchedaLiberatorie />

      <section className="scheda-account">
        <h2 className="scheda-titolo">{t("account.piano")}</h2>
        <p className="piano-attuale" data-piano={io.piano}>
          {t(`piani.nome_${io.piano}`)}
          {io.pianoScade && <span className="nota"> · {t("account.piano_fino", { data: data.format(new Date(io.pianoScade)) })}</span>}
        </p>
        {io.quota.illimitate ? (
          <p className="nota">{t("account.partite_illimitate")}</p>
        ) : (
          <p className="nota">{t("account.partite_oggi", { restanti: io.quota.restanti, limite: io.quota.limite })}</p>
        )}
        {io.piano === "base" ? (
          <button type="button" className="pulsante pulsante-primario" onClick={onPiani}>
            {t("piani.scopri")}
          </button>
        ) : io.piano === "silver" && goldInVendita ? (
          <button type="button" className="pulsante pulsante-primario" onClick={onPiani}>
            {t("piani.passa_gold")}
          </button>
        ) : null}
        {/* v037: tolto «I miei filtri» (lo Store è già nella scheda dei crediti) */}
        {io.piano !== "base" && io.pianoFonte === "revenuecat" && <p className="nota">{t("account.gestisci_abbonamento")}</p>}
      </section>

      {/* v037: testo grande */}
      <section className="scheda-account">
        <h2 className="scheda-titolo">{t("account.accessibilita")}</h2>
        {/* v043: due parole su cosa fa */}
        <p className="nota">{t("aspetto.testo_grande_nota")}</p>
        <InterruttoreTestoGrande />
      </section>

      {/* v038: notifiche */}
      <section className="scheda-account">
        <h2 className="scheda-titolo">{t("notifiche.titolo")}</h2>
        <InterruttoreNotifiche />
      </section>

      <section className="scheda-account azioni-colonna">
        <button type="button" className="pulsante pulsante-secondario" onClick={azione(scaricaDati)}>
          {t("account.scarica_dati")}
        </button>
        <button type="button" className="pulsante pulsante-secondario" onClick={azione(() => esci(false))}>
          {t("account.esci")}
        </button>
        <button type="button" className="pulsante pulsante-secondario" onClick={azione(() => esci(true))}>
          {t("account.esci_ovunque")}
        </button>
        <button type="button" className="pulsante pulsante-pericolo" onClick={() => setCancella(true)}>
          {t("account.cancella")}
        </button>
      </section>
      {errore && (
        <p className="admin-errore" role="alert">
          {t(`account.errore_${errore}`, { defaultValue: t("account.errore_generico") })}
        </p>
      )}

      <Finestra aperta={cancella} titolo={t("account.cancella")} onChiudi={() => setCancella(false)}>
        <p>{t("account.cancella_testo")}</p>
        {io.piano !== "base" && io.pianoFonte === "revenuecat" && <p className="nota">{t("account.cancella_abbonamento")}</p>}
        <div className="azioni-riga">
          <button type="button" className="pulsante pulsante-secondario" onClick={() => setCancella(false)}>
            {t("generale.annulla")}
          </button>
          <button type="button" className="pulsante pulsante-pericolo" onClick={azione(cancellaAccount)}>
            {t("account.cancella_conferma")}
          </button>
        </div>
      </Finestra>
    </main>
  );
}
